import { getCompanies, handleResponse } from '../../api';
import { action, observable, computed } from 'mobx';
import { keyBy, find, orderBy, uniq } from 'lodash';

const COMPANY_ATTRIBUTES = [
  ['id', 'id'],
  ['status', 'status'],
  ['runtime', 'runtime'],
  ['inactivityReason', 'inactivity_reason'],
  ['createdAt', 'created_at'],
  ['updatedAt', 'updated_at'],
  ['name', 'name'],
  ['website', 'website'],
  ['description', 'description'],
  ['logo', 'logo'],
  ['membership', 'membership'],
  ['parents', 'parents'],
  ['certificates', 'certificates'],
  ['addresses', 'addresses'],
];

const CERTIFICATES_BY_CONTEXT = {
  holzbriketts: ['briquet_certificate'],
  hackschnitzel: ['hackschnitzel_certificate'],
  pellets: [
    'producer_certificate',
    'dealer_certificate',
    'service_certificate',
  ],
  depi: [],
  depv: [],
};

class Company {
  type = 'django-unternehmen';

  constructor(props) {
    COMPANY_ATTRIBUTES.forEach(pair => {
      this[pair[0]] = props[pair[1]];
    });
  }

  static fromJson(props) {
    const matchedProps = Object.keys(props).reduce((acc, key) => {
      const index = COMPANY_ATTRIBUTES.findIndex(pair => pair[0] === key);
      if (index > -1) {
        acc[COMPANY_ATTRIBUTES[index][1]] = props[key];
      } else {
        acc[key] = props[key];
      }
      return acc;
    }, {});
    return new Company(matchedProps);
  }

  toFilterEntry = () => {
    return {
      uuid: this.id,
      title: this.name,
      type: this.type,
      status: this.status,
      runtime: this.runtime,
      // subtitle: page.data.subtitle,
      createdAt: this.createdAt,
      address: this.address,
      productIds: this.productIds,
      productNames: this.productNames,
      fakeProductNames: this.fakeProductNames,
      certificates: this.certificates,
      membership: this.membership,
      categories: this.categories,
      inactivityReason: this.inactivityReason,
      // place: page.data.place,
      // address: page.data.address,
      // date: page.data.date,
      // startDate: page.data.startDate || null,
      // endDate: page.data.endDate || null,
      abstract: this.description,
      website: this.website,
      // tags: page.tags,
      mediaId: this.logo,
      idents: this.idents,
    };
  };

  get address() {
    // return adresses with logic
    if (this.addresses.length > 1)
      return this.addresses.find(a => a.type === 'headquarters');
    return this.addresses[0];
  }

  get idents() {
    return this.certificates
      .map(c => c.ident.trim())
      .filter(i => i.trim() !== '')
      .join(', ');
  }

  get productIds() {
    return context =>
      this.certificates
        .filter(c => CERTIFICATES_BY_CONTEXT[context].indexOf(c.type) > -1)
        .map(c => c.ident.trim())
        .filter(i => i.trim() !== '')
        .join(', ');
  }

  get productNames() {
    return this.certificates
      .filter(c => c.products)
      .map(c =>
        c.products
          .filter(p => p.name.length > 0)
          .map(p => p.name)
          .join(', '),
      )
      .join(', ');
  }

  get categories() {
    return uniq(
      this.certificates.filter(c => !!c.category).map(c => c.category),
    );
  }

  get fakeProductNames() {
    return this.certificates
      .filter(c => c.product_name)
      .map(c => c.product_name)
      .join(', ');
  }

  get hasSchnitzel() {
    return this.certificates.some(c => c.type === 'hackschnitzel_certificate');
  }
  get hasBriquets() {
    return this.certificates.some(c => c.type === 'briquet_certificate');
  }
  get hasPellets() {
    return this.certificates.some(
      c =>
        [
          'producer_certificate',
          'dealer_certificate',
          'service_certificate',
        ].indexOf(c.type) > -1,
    );
  }
}

class Companies {
  @observable
  isLoading = false;
  @observable
  _asList = [];

  initialize = async () => {
    if (this.isLoaded) return;
    await this.fetchData();
  };

  hydrate = ({ _asList }) => {
    console.log(_asList);
    if (_asList) this._asList = _asList.map(c => Company.fromJson(c));
  };

  @action.bound
  async fetchData() {
    try {
      this.isLoading = true;
      const response = await getCompanies();
      handleResponse([
        [
          200,
          () => {
            this.receiveData(response);
          },
        ],
        [
          'default',
          () => {
            console.error(response);
          },
        ],
      ])(response);
      this.isLoading = false;
      // const json = await response.json();
    } catch (e) {
      console.warn(e);
    }
  }

  @action.bound
  receiveData({ data }) {
    try {
      // const parsed = csvParse(data);
      this._asList = data.map(c => new Company(c));
    } catch (e) {
      console.warn(e);
    }
  }

  @computed
  get isLoaded() {
    return this.asList.length > 0;
  }

  @computed
  get byId() {
    return keyBy(this.asList, 'id');
  }

  @computed
  get asList() {
    return orderBy(
      this._asList.map(c =>
        Company.fromJson({
          ...c,
          certificates: c.certificates.filter(c => c.status === 'active'),
        }),
      ),
      'title',
    );
  }

  @computed
  get asFilterEntries() {
    return this.asList.map(c => c.toFilterEntry());
  }

  @computed
  get schnitzelOnly() {
    return this.asList.filter(c => c.hasSchnitzel).map(c => c.toFilterEntry());
  }

  @computed
  get pelletsOnly() {
    return this.asList.filter(c => c.hasPellets).map(c => c.toFilterEntry());
  }

  @computed
  get briquetsOnly() {
    return this.asList.filter(c => c.hasBriquets).map(c => c.toFilterEntry());
  }

  @computed
  get all() {
    return this.asList
      .filter(c => c.membership.length > 0)
      .map(c => c.toFilterEntry());
  }

  @computed
  get getDaughters() {
    return parentId => this.asList.filter(c => c.parents === parentId);
  }

  @computed
  get getParent() {
    return parentId => this.asList.find(c => c.id === parentId);
  }
}

export default Companies;
